import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { 
  Sparkles, Film, Tv, Clapperboard, Crown, Loader2, 
  Laugh, Brain, Heart, Sword, Ghost, Rocket, Search,
  Gem, Clock, Shuffle, ChevronDown, ChevronUp, X, Filter, Lock,
  Bookmark, BookmarkPlus, RefreshCw, Trash2
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { requestTicket } from "@/lib/guest";
import { useUserRole } from "@/hooks/useUserRole";
import { useUsageLimit } from "@/hooks/useUsageLimit";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import CurtainDown from "@/components/CurtainDown";
import { useServiceDown } from "@/hooks/useServiceHealth";

interface QuickDiscoveryProps {
  onDiscover: (params: DiscoveryParams) => void;
  isLoading?: boolean;
  isPremium?: boolean;
}

export interface DiscoveryParams {
  moods: string[]; // Changed to array for multi-select
  mediaType: 'movie' | 'tv' | 'all';
  /** null = "Any" — no popularity constraint at all */
  obscurity: number | null;
  /** null = "Any" — no release-window constraint at all */
  era: number | null;
  textQuery?: string;
  keywords?: string;
  genres?: string[];
  runtime?: { min: number; max: number };
  language?: string;
  /** VIP re-roll of the previous request — never spends a monthly search. */
  reroll?: boolean;
}

/** Free members can dial the vault this deep; below-the-radar depth is a VIP dial. */
const FREE_OBSCURITY_CAP = 60;
/** Free members can combine this many genres in one search. */
const FREE_GENRE_CAP = 2;
const PRESETS_KEY = 'swiftix_qd_presets';
const MAX_PRESETS = 6;

interface DiscoveryPreset {
  id: string;
  name: string;
  params: {
    moods: string[];
    mediaType: 'movie' | 'tv' | 'all';
    obscurity: number | null;
    era: number | null;
    textQuery: string;
    keywords: string;
    genres: string[];
    runtime: [number, number];
    language: string;
  };
}

const loadPresets = (): DiscoveryPreset[] => {
  try {
    const raw = localStorage.getItem(PRESETS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PRESETS) : [];
  } catch {
    return [];
  }
};

const MOOD_PRESETS = [
  { id: 'laugh', label: 'Make Me Laugh', icon: Laugh, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', description: 'Actually funny comedies' },
  { id: 'think', label: 'Mind-Bending', icon: Brain, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', description: 'Twists & psychological' },
  { id: 'feel', label: 'Hit The Feels', icon: Heart, color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', description: 'Emotional & moving' },
  { id: 'thrill', label: 'Edge of Seat', icon: Sword, color: 'bg-red-500/20 text-red-400 border-red-500/30', description: 'Intense & gripping' },
  { id: 'scare', label: 'Scare Me', icon: Ghost, color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', description: 'Horror & creepy' },
  { id: 'escape', label: 'Epic Escape', icon: Rocket, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', description: 'Fantasy worlds' },
  { id: 'scifi', label: 'Sci-Fi', icon: Rocket, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', description: 'Science fiction' },
];

// Note: Animation is handled server-side now; we still show the option but 
// the server excludes it automatically for mood-based searches unless explicitly requested
const GENRES = [
  'Action', 'Adventure', 'Animation', 'Comedy', 'Crime', 'Documentary',
  'Drama', 'Family', 'Fantasy', 'Horror', 'Mystery', 'Romance',
  'Sci-Fi', 'Thriller', 'War', 'Western'
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'hi', label: 'Hindi' },
  { code: 'zh', label: 'Chinese' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
];

const getObscurityLabel = (value: number | null) => {
  if (value === null) return 'Any Popularity';
  if (value < 20) return 'Popular Hits';
  if (value < 40) return 'Well-Known';
  if (value < 60) return 'Under the Radar';
  if (value < 80) return 'Hidden Gems';
  return 'Deep Cuts';
};

const getEraLabel = (value: number | null) => {
  if (value === null) return 'Any Era';
  if (value < 20) return 'Classic (Pre-2000)';
  if (value < 40) return '2000s Era';
  if (value < 60) return '2010s';
  if (value < 80) return 'Recent (2020+)';
  return 'Brand New (last 2 years)';
};

const getRuntimeLabel = (min: number, max: number) => {
  if (min === 0 && max === 300) return 'Any Length';
  if (max <= 90) return 'Short (<90 min)';
  if (min >= 120) return 'Long (2+ hrs)';
  return `${min}-${max} min`;
};

export const QuickDiscovery = ({ onDiscover, isLoading, isPremium = false }: QuickDiscoveryProps) => {
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const [mediaType, setMediaType] = useState<'movie' | 'tv' | 'all'>('movie');
  // null = "Any" — the dial imposes no constraint at all
  const [obscurity, setObscurity] = useState<number | null>(null);
  const [era, setEra] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [textQuery, setTextQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [runtime, setRuntime] = useState<[number, number]>([0, 300]);
  const [language, setLanguage] = useState<string>('');
  const [keywords, setKeywords] = useState('');
  const [presets, setPresets] = useState<DiscoveryPreset[]>(() => loadPresets());
  const [lastParams, setLastParams] = useState<DiscoveryParams | null>(null);
  const [rerolls, setRerolls] = useState(0);
  
  const { user } = useAuth();
  const { isOwner } = useUserRole(user?.id);
  const navigate = useNavigate();
  
  // Usage tracking
  const { 
    currentUsage: searchUsage, 
    limit: searchLimit, 
    remaining: searchRemaining,
    percentage: searchPercentage,
    isLimitReached: isSearchLimitReached,
    incrementUsage: incrementSearchUsage 
  } = useUsageLimit('quick_discover');

  // Free members get a small monthly taste of the VIP dials, then they lock.
  const {
    limit: vipTasteLimit,
    remaining: vipTasteRemaining,
    isLimitReached: vipTasteSpent,
    incrementUsage: incrementVipTaste,
  } = useUsageLimit('quick_discover_premium');

  // VIP dials: vault depth (deep obscurity) and genre stacking beyond two.
  const deepVault = obscurity !== null && obscurity > FREE_OBSCURITY_CAP;
  const stackedGenres = selectedGenres.length > FREE_GENRE_CAP;
  const isUsingPremiumFeatures = deepVault || stackedGenres;

  // Mood stacking is a VIP perk: free members search one mood at a time, VIP blends up to 5.
  const MAX_MOODS = isPremium ? 5 : 1;

  const upgradeAction = {
    label: "See VIP",
    onClick: () => navigate("/vip?from=quick-discovery"),
  };

  const savePresets = (next: DiscoveryPreset[]) => {
    setPresets(next);
    try {
      localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — presets stay in memory for this visit */
    }
  };

  const currentPresetParams = (): DiscoveryPreset['params'] => ({
    moods: selectedMoods,
    mediaType,
    obscurity,
    era,
    textQuery,
    keywords,
    genres: selectedGenres,
    runtime,
    language,
  });

  const presetLabel = () => {
    const moodLabels = selectedMoods
      .map((id) => MOOD_PRESETS.find((m) => m.id === id)?.label)
      .filter(Boolean) as string[];
    if (moodLabels.length) return moodLabels.join(' + ');
    if (selectedGenres.length) return selectedGenres.join(' + ');
    if (textQuery.trim()) return textQuery.trim().slice(0, 28);
    if (keywords.trim()) return keywords.trim().slice(0, 28);
    return `Setup ${presets.length + 1}`;
  };

  const handleSavePreset = () => {
    if (!isPremium) {
      toast.error("Saved setups are a VIP perk", {
        description: "VIP members keep their favourite mood and dial combinations one tap away.",
        action: upgradeAction,
      });
      return;
    }
    if (presets.length >= MAX_PRESETS) {
      toast.error(`You can keep ${MAX_PRESETS} saved setups`, {
        description: "Remove one to make room for this.",
      });
      return;
    }
    savePresets([...presets, { id: crypto.randomUUID(), name: presetLabel(), params: currentPresetParams() }]);
    toast.success("Setup saved to your shelf");
  };

  const applyPreset = (preset: DiscoveryPreset) => {
    const p = preset.params;
    setSelectedMoods(p.moods ?? []);
    setMediaType(p.mediaType ?? 'movie');
    setObscurity(p.obscurity ?? null);
    setEra(p.era ?? null);
    setTextQuery(p.textQuery ?? '');
    setKeywords(p.keywords ?? '');
    setSelectedGenres(p.genres ?? []);
    setRuntime(p.runtime ?? [0, 300]);
    setLanguage(p.language ?? '');
  };

  const deletePreset = (id: string) => savePresets(presets.filter((p) => p.id !== id));

  const handleMoodClick = (moodId: string) => {
    setSelectedMoods(prev => {
      if (prev.includes(moodId)) return prev.filter(m => m !== moodId);
      if (prev.length >= MAX_MOODS) {
        if (!isPremium) {
          // Free members pick one mood at a time — switching is normal, not an error.
          return [moodId];
        }
        return [...prev.slice(1), moodId];
      }
      return [...prev, moodId];
    });
  };

  const handleGenreClick = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleDiscover = async () => {
    if (!user) {
      requestTicket('use Quick Discovery');
      return;
    }
    if (selectedMoods.length === 0 && !textQuery.trim() && !keywords.trim() && selectedGenres.length === 0 && !language && (runtime[0] === 0 && runtime[1] === 300)) return;
    
    // Check search limit
    if (isSearchLimitReached) {
      toast.error("Monthly limit reached", {
        description: isPremium 
          ? "You've used all 100 searches this month. Resets on the 1st." 
          : "Upgrade to VIP for 100 searches/month.",
        action: !isPremium ? {
          label: "See VIP",
          onClick: () => navigate("/vip?from=quick-discovery")
        } : undefined
      });
      return;
    }
    
    // VIP dials: free members spend one of their monthly previews. When those run
    // out the search still runs — the VIP dials just dim back to the free range.
    let effectiveObscurity = obscurity;
    let effectiveGenres = selectedGenres;
    if (!isPremium && isUsingPremiumFeatures) {
      if (vipTasteSpent) {
        if (deepVault) effectiveObscurity = FREE_OBSCURITY_CAP;
        if (stackedGenres) effectiveGenres = selectedGenres.slice(0, FREE_GENRE_CAP);
        toast.info("VIP dials dimmed for this search", {
          description: `You've used all ${vipTasteLimit} VIP previews this month, so we searched the free range instead. VIP keeps vault depth and genre stacking open every day.`,
          action: upgradeAction,
        });
      } else {
        const tasted = await incrementVipTaste();
        if (tasted) {
          toast.success("VIP preview unlocked for this search", {
            description: `${Math.max(0, vipTasteRemaining - 1)} of ${vipTasteLimit} left this month.`,
            action: upgradeAction,
          });
        } else {
          if (deepVault) effectiveObscurity = FREE_OBSCURITY_CAP;
          if (stackedGenres) effectiveGenres = selectedGenres.slice(0, FREE_GENRE_CAP);
        }
      }
    }
    
    // Increment usage
    const searchIncremented = await incrementSearchUsage();
    if (!searchIncremented) {
      toast.error("Could not process search. Please try again.");
      return;
    }
    
    const params: DiscoveryParams = {
      moods: selectedMoods,
      mediaType,
      obscurity: effectiveObscurity,
      era,
      textQuery: textQuery.trim() || undefined,
      keywords: keywords.trim() || undefined,
      genres: effectiveGenres.length > 0 ? effectiveGenres : undefined,
      runtime: runtime[0] !== 0 || runtime[1] !== 300 ? { min: runtime[0], max: runtime[1] } : undefined,
      language: language || undefined,
    };
    setLastParams(params);
    setRerolls(0);
    onDiscover(params);
  };

  /** VIP perk: reshuffle the same request without spending a monthly search. */
  const handleReroll = () => {
    if (!lastParams) return;
    if (!isPremium) {
      toast.error("Free re-rolls are a VIP perk", {
        description: "VIP members can reshuffle the same request as often as they like — it never costs a search.",
        action: upgradeAction,
      });
      return;
    }
    if (featureClosed || isLoading) return;
    setRerolls((n) => n + 1);
    onDiscover({ ...lastParams, reroll: true });
  };

  const handleSurpriseMe = async () => {
    if (!user) {
      requestTicket('use Quick Discovery');
      return;
    }
    // Check search limit
    if (isSearchLimitReached) {
      toast.error("Monthly limit reached", {
        description: isPremium 
          ? "You've used all 100 searches this month." 
          : "Upgrade to VIP for 100 searches/month.",
        action: !isPremium ? {
          label: "See VIP",
          onClick: () => navigate("/vip?from=quick-discovery")
        } : undefined
      });
      return;
    }
    
    const searchIncremented = await incrementSearchUsage();
    if (!searchIncremented) return;
    
    const randomMood = MOOD_PRESETS[Math.floor(Math.random() * MOOD_PRESETS.length)];
    setSelectedMoods([randomMood.id]);
    
    // Keep the dice inside the free range for free members so a random roll never
    // parks a VIP-only depth in the dial and silently eats their previews later.
    const maxObscurity = isPremium ? 100 : FREE_OBSCURITY_CAP;
    const randomObscurity = Math.floor(Math.random() * maxObscurity);
    const randomEra = Math.floor(Math.random() * 100);
    
    setObscurity(randomObscurity);
    setEra(randomEra);
    
    const params: DiscoveryParams = {
      moods: [randomMood.id],
      mediaType,
      obscurity: randomObscurity,
      era: randomEra,
    };
    setLastParams(params);
    setRerolls(0);
    onDiscover(params);
  };

  const handleMediaTypeChange = (value: string) => {
    const newType = value as 'movie' | 'tv' | 'all';
    if ((newType === 'tv' || newType === 'all') && !isPremium) {
      return;
    }
    setMediaType(newType);
  };

  const clearAllFilters = () => {
    setSelectedMoods([]);
    setSelectedGenres([]);
    setTextQuery('');
    setKeywords('');
    setObscurity(null);
    setEra(null);
    setRuntime([0, 300]);
    setLanguage('');
  };

  const { isDown: discoveryDown } = useServiceDown('ai');
  const { isDown: catalogueDown } = useServiceDown('tmdb');
  const featureClosed = discoveryDown || catalogueDown;

  const hasActiveFilters = selectedMoods.length > 0 || selectedGenres.length > 0 || textQuery || keywords || language || runtime[0] !== 0 || runtime[1] !== 300 || obscurity !== null || era !== null;

  return (
    <div className="w-full max-w-5xl mx-auto" data-tutorial="quick-discovery">
      <CurtainDown service="ai" feature="Quick Discovery" className="mb-4" />
      <CurtainDown service="tmdb" feature="The catalogue" className="mb-4" />
      {/* ============ QUICK DISCOVERY ============ */}
      <div className="relative">
        {/* Top marquee bulb strip */}
        <div
          aria-hidden
          className="absolute -top-2 left-6 right-6 h-1 flex justify-between items-center px-2 z-20"
        >
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="marquee-bulb"
              style={{ animationDelay: `${(i % 6) * 0.4}s` }}
            />
          ))}
        </div>

        <div className="vip-ticket relative overflow-hidden film-grain">
          {/* Ambient cinema atmospherics */}
          <div className="spotlight-beam spotlight-flicker pointer-events-none" />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent 0 22px, hsl(var(--marquee-bulb) / 0.35) 22px 23px)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full blur-3xl"
            style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.18), transparent 70%)" }}
          />

          <div className="relative z-10 px-3 sm:px-6 pt-1.5 sm:pt-2 pb-2.5 sm:pb-3 space-y-2.5 sm:space-y-3">
            {/* ============ MARQUEE HEADER ============ */}
            <div className="text-center space-y-1">
              <span className="cinema-eyebrow inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-marquee-bulb/10 text-marquee-bulb border border-marquee-bulb/40">
                <span className="marquee-bulb" />
                Now Booking · Quick Discovery
                <span className="marquee-bulb" />
              </span>
              <h2 className="cinema-headline text-xl sm:text-3xl text-foreground">
                Choose <span className="text-marquee-bulb marquee-glow">tonight's</span> screening
              </h2>
              <p className="text-[11px] sm:text-xs text-foreground/65 max-w-xl mx-auto leading-snug">
                Stamp the moods. Turn the dials. The projectionist will find your film.
              </p>
            </div>

            {/* ============ REEL COUNTER (usage) — members only ============ */}
            {user && (
            <div
              className={cn(
                "relative rounded-lg border px-3 py-2 backdrop-blur-sm overflow-hidden",
                isSearchLimitReached
                  ? "bg-destructive/10 border-destructive/40"
                  : searchPercentage >= 60
                  ? "bg-warning/10 border-warning/40"
                  : "bg-background/40 border-marquee-bulb/20"
              )}
            >
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="relative h-6 w-6 rounded-full border border-marquee-bulb/40 bg-card flex items-center justify-center flex-shrink-0">
                    <span className="block h-1.5 w-1.5 rounded-full bg-marquee-bulb animate-[bulb-pulse_2.4s_ease-in-out_infinite]" />
                  </div>
                  <span className="cinema-eyebrow text-foreground/80 truncate">
                    Reel {searchRemaining}/{searchLimit} · this month
                  </span>
                </div>
                {!isPremium && (
                  <Link to="/vip?from=quick-discovery" className="flex-shrink-0">
                    <Badge className="text-[10px] cursor-pointer border-accent/50 bg-gradient-to-r from-accent/20 to-primary/20 hover:from-accent/30 hover:to-primary/30 transition-all">
                      <Crown className="h-3 w-3 mr-1 text-premium" />
                      <span className="bg-gradient-to-r from-accent via-primary to-accent bg-[length:200%_auto] animate-[gradient-shift_2s_ease-in-out_infinite] bg-clip-text text-transparent font-bold">
                        Get 100/mo
                      </span>
                    </Badge>
                  </Link>
                )}
              </div>
              <Progress value={searchPercentage} className="h-1" />
            </div>
            )}

            {/* ============ PROJECTIONIST INPUT ============ */}
            <div className="relative" data-tutorial="discovery-search">
              <div className="absolute -left-1 top-1/2 -translate-y-1/2 h-3/5 w-1 rounded-full bg-gradient-to-b from-marquee-bulb via-marquee-bulb/70 to-transparent" />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-marquee-bulb/70" />
              <Input
                placeholder="Tell the projectionist… ‘heist movie with a twist’"
                value={textQuery}
                onChange={(e) => setTextQuery(e.target.value)}
                className="pl-10 pr-10 h-13 sm:h-14 text-sm bg-background/60 border-marquee-bulb/25 focus:border-marquee-bulb/60 focus:ring-marquee-bulb/30 rounded-xl placeholder:text-foreground/35"
                onKeyDown={(e) => e.key === "Enter" && handleDiscover()}
              />
              {textQuery && (
                <button
                  onClick={() => setTextQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* ============ FORMAT TABS (Movies / TV / All) ============ */}
            <div className="flex justify-center">
              <Tabs value={mediaType} onValueChange={handleMediaTypeChange} className="w-full max-w-lg">
                <TabsList className="grid w-full grid-cols-3 bg-background/40 border border-marquee-bulb/20 p-0.5 rounded-lg h-auto">
                  <TabsTrigger value="movie" className="tab-ticket">
                    <Film className="h-4 w-4" />
                    Movies
                  </TabsTrigger>
                  <TabsTrigger
                    value="tv"
                    disabled={!isPremium}
                    className={cn("tab-ticket", !isPremium && "opacity-50 cursor-not-allowed")}
                  >
                    <Tv className="h-4 w-4" />
                    TV
                    {!isPremium && <Crown className="h-3 w-3 ml-1 text-premium" />}
                  </TabsTrigger>
                  <TabsTrigger
                    value="all"
                    disabled={!isPremium}
                    className={cn("tab-ticket", !isPremium && "opacity-50 cursor-not-allowed")}
                  >
                    <Clapperboard className="h-4 w-4" />
                    All
                    {!isPremium && <Crown className="h-3 w-3 ml-1 text-premium" />}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* ============ MOOD TICKET STUBS ============ */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="cinema-eyebrow text-foreground/70">
                  {isPremium
                    ? 'Stamp your ticket · blend up to 5 moods'
                    : 'Stamp your ticket · pick one mood'}
                </span>
                {selectedMoods.length > 0 && (
                  <button
                    onClick={() => setSelectedMoods([])}
                    className="text-[10px] uppercase tracking-wider text-foreground/50 hover:text-foreground"
                  >
                    Clear ({selectedMoods.length})
                  </button>
                )}
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
                data-tutorial="mood-buttons"
              >
                {MOOD_PRESETS.map((mood) => {
                  const Icon = mood.icon;
                  const isSelected = selectedMoods.includes(mood.id);
                  return (
                    <button
                      key={mood.id}
                      onClick={() => handleMoodClick(mood.id)}
                      className={cn(
                        "group relative flex flex-col items-center justify-center gap-1 p-2 sm:p-2.5 rounded-lg border transition-all duration-300 overflow-hidden",
                        "hover:-translate-y-0.5 active:scale-[0.97]",
                        isSelected
                          ? "bg-gradient-to-br from-marquee-bulb/15 via-accent/10 to-marquee-bulb/15 border-marquee-bulb/60 shadow-[0_0_20px_-4px_hsl(var(--marquee-bulb)/0.5)]"
                          : "bg-background/40 border-border/40 hover:border-marquee-bulb/40 hover:bg-background/60"
                      )}
                    >
                      {/* perforated ticket edge */}
                      <span
                        aria-hidden
                        className="absolute inset-x-3 bottom-1.5 h-px opacity-50"
                        style={{
                          backgroundImage:
                            "radial-gradient(circle, hsl(var(--foreground) / 0.35) 0.6px, transparent 1.4px)",
                          backgroundSize: "6px 2px",
                          backgroundRepeat: "repeat-x",
                        }}
                      />
                      <Icon
                        className={cn(
                          "h-4 w-4 sm:h-5 sm:w-5 transition-all",
                          isSelected ? "text-marquee-bulb scale-110 drop-shadow-[0_0_8px_hsl(var(--marquee-bulb)/0.7)]" : "text-foreground/70 group-hover:text-foreground"
                        )}
                      />
                      <span className={cn(
                        "text-[11px] sm:text-xs font-bold uppercase tracking-wide text-center leading-tight",
                        isSelected ? "text-foreground" : "text-foreground/75"
                      )}>
                        {mood.label}
                      </span>
                      {isSelected && (
                        <span
                          aria-hidden
                          className="absolute -top-1 -right-1 marquee-bulb"
                          style={{ width: 6, height: 6 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ============ KEYWORDS (concise) ============ */}
            <div className="space-y-1.5">
              <span className="cinema-eyebrow text-foreground/70 block">
                Add a clue · optional keywords
              </span>
              <Input
                placeholder="e.g., time travel, heist, underdog, revenge"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="h-9 bg-background/40 border-marquee-bulb/20 focus:border-marquee-bulb/50 rounded-lg"
                onKeyDown={(e) => e.key === "Enter" && handleDiscover()}
              />
            </div>

            {/* ============ THE BOOTH (advanced) ============ */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="group w-full flex items-center justify-center gap-2 py-1.5 rounded-lg border border-dashed border-marquee-bulb/25 hover:border-marquee-bulb/50 hover:bg-marquee-bulb/5 transition-all"
              data-tutorial="filters-toggle"
            >
              <Filter className="h-3.5 w-3.5 text-marquee-bulb/70 group-hover:text-marquee-bulb" />
              <span className="cinema-eyebrow text-foreground/70 group-hover:text-foreground">
                {showAdvanced ? "Close the booth" : "Open the booth · more dials"}
              </span>
              {showAdvanced ? (
                <ChevronUp className="h-3.5 w-3.5 text-marquee-bulb/70" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-marquee-bulb/70" />
              )}
            </button>

            {showAdvanced && (
              <div
                className="space-y-6 pt-4 border-t border-marquee-bulb/15"
                data-tutorial="genre-filters"
              >
                {/* Genres */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="cinema-eyebrow text-foreground/70">
                      Genres
                      <span className="ml-2 normal-case tracking-normal text-[10px] text-foreground/45">
                        {isPremium ? 'stack as many as you like' : `two free · beyond that is VIP`}
                      </span>
                    </span>
                    {selectedGenres.length > 0 && (
                      <button
                        onClick={() => setSelectedGenres([])}
                        className="text-[10px] uppercase tracking-wider text-foreground/50 hover:text-foreground"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {GENRES.map((genre) => {
                      const selected = selectedGenres.includes(genre);
                      // Third+ genre in one search is a VIP dial for free members
                      const vipSlot = !isPremium && selected && selectedGenres.indexOf(genre) >= FREE_GENRE_CAP;
                      return (
                        <button
                          key={genre}
                          onClick={() => handleGenreClick(genre)}
                          className={cn(
                            "inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all border",
                            selected
                              ? vipSlot
                                ? "bg-accent/15 text-accent border-accent/60 shadow-[0_0_12px_-4px_hsl(var(--accent)/0.6)]"
                                : "bg-marquee-bulb/15 text-marquee-bulb border-marquee-bulb/60 shadow-[0_0_12px_-4px_hsl(var(--marquee-bulb)/0.6)]"
                              : "bg-background/40 text-foreground/65 border-border/40 hover:border-marquee-bulb/40 hover:text-foreground"
                          )}
                        >
                          {vipSlot && <Crown className="h-3 w-3 text-premium" />}
                          {genre}
                        </button>
                      );
                    })}
                  </div>
                  {stackedGenres && !isPremium && (
                    <p className="text-[10px] text-accent/90">
                      Stacking {selectedGenres.length} genres is a VIP dial — this search will use one of your {vipTasteLimit} monthly previews.
                    </p>
                  )}
                </div>

                {/* Dial: Runtime */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-marquee-bulb" />
                      <span className="cinema-eyebrow text-foreground/70">Runtime dial</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-marquee-bulb/30 text-marquee-bulb">
                      {getRuntimeLabel(runtime[0], runtime[1])}
                    </Badge>
                  </div>
                  <Slider
                    value={runtime}
                    onValueChange={(value) => setRuntime(value as [number, number])}
                    min={0}
                    max={300}
                    step={15}
                  />
                  <div className="flex justify-between text-[10px] uppercase tracking-wider text-foreground/45 px-1">
                    <span>Short</span>
                    <span>Epic</span>
                  </div>
                </div>

                {/* Languages */}
                <div className="space-y-2">
                  <span className="cinema-eyebrow text-foreground/70 block">Language reel</span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => setLanguage("")}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all border",
                        !language
                          ? "bg-marquee-bulb/15 text-marquee-bulb border-marquee-bulb/60"
                          : "bg-background/40 text-foreground/65 border-border/40 hover:border-marquee-bulb/40"
                      )}
                    >
                      Any
                    </button>
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => setLanguage(lang.code)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all border",
                          language === lang.code
                            ? "bg-marquee-bulb/15 text-marquee-bulb border-marquee-bulb/60"
                            : "bg-background/40 text-foreground/65 border-border/40 hover:border-marquee-bulb/40"
                        )}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dial: Obscurity */}
                <div className="space-y-3" data-tutorial="obscurity-slider">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Gem className="h-3.5 w-3.5 text-marquee-bulb" />
                      <span className="cinema-eyebrow text-foreground/70">Obscurity dial</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px]",
                          deepVault && !isPremium
                            ? "border-accent/50 text-accent"
                            : "border-marquee-bulb/30 text-marquee-bulb"
                        )}
                      >
                        {deepVault && !isPremium && <Crown className="h-2.5 w-2.5 mr-1 text-premium" />}
                        {getObscurityLabel(obscurity)}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => setObscurity(prev => (prev === null ? 50 : null))}
                        aria-pressed={obscurity === null}
                        className={cn(
                          "text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors",
                          obscurity === null
                            ? "border-marquee-bulb/60 bg-marquee-bulb/15 text-marquee-bulb"
                            : "border-border/60 text-foreground/50 hover:text-foreground/80"
                        )}
                      >
                        Any
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    {/* Vault depth zone — free members can peek, VIP lives there */}
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -top-1 bottom-0 right-0 rounded-r-full border-l border-dashed border-accent/40 bg-gradient-to-r from-transparent to-accent/10"
                      style={{ width: `${100 - FREE_OBSCURITY_CAP}%` }}
                    />
                    <Slider
                      value={[obscurity ?? 50]}
                      onValueChange={([value]) => setObscurity(value)}
                      min={0}
                      max={100}
                      step={1}
                      disabled={obscurity === null}
                      className={cn("relative", obscurity === null && "opacity-40")}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] uppercase tracking-wider text-foreground/45 px-1">
                    <span>Mainstream</span>
                    <span className={cn(!isPremium && "text-accent/80")}>
                      {isPremium ? 'Underground' : 'Vault depth · VIP'}
                    </span>
                  </div>
                  {deepVault && !isPremium && (
                    <p className="text-[10px] text-accent/90">
                      You're in the vault — hidden gems and deep cuts are a VIP dial. This search will use one of your {vipTasteLimit} monthly previews.
                    </p>
                  )}
                </div>

                {/* Dial: Era */}
                <div className="space-y-3" data-tutorial="era-slider">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-marquee-bulb" />
                      <span className="cinema-eyebrow text-foreground/70">Era dial</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] border-marquee-bulb/30 text-marquee-bulb">
                        {getEraLabel(era)}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => setEra(prev => (prev === null ? 70 : null))}
                        aria-pressed={era === null}
                        className={cn(
                          "text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border transition-colors",
                          era === null
                            ? "border-marquee-bulb/60 bg-marquee-bulb/15 text-marquee-bulb"
                            : "border-border/60 text-foreground/50 hover:text-foreground/80"
                        )}
                      >
                        Any
                      </button>
                    </div>
                  </div>
                  <Slider
                    value={[era ?? 70]}
                    onValueChange={([value]) => setEra(value)}
                    min={0}
                    max={100}
                    step={1}
                    disabled={era === null}
                    className={cn(era === null && "opacity-40")}
                  />
                  <div className="flex justify-between text-[10px] uppercase tracking-wider text-foreground/45 px-1">
                    <span>Classic</span>
                    <span>Brand New</span>
                  </div>
                </div>


                {/* Saved setups — VIP shelf */}
                <div
                  className="space-y-3 pt-4 border-t border-marquee-bulb/15"
                  data-tutorial="premium-filters"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Bookmark className="h-3.5 w-3.5 text-marquee-bulb" />
                      <span className="cinema-eyebrow text-foreground/80">Saved setups</span>
                      {!isPremium && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-accent/50 bg-gradient-to-r from-accent/20 to-primary/20">
                          <Crown className="h-2.5 w-2.5 mr-0.5 text-premium" />
                          <span className="bg-gradient-to-r from-accent via-primary to-accent bg-[length:200%_auto] animate-[gradient-shift_2s_ease-in-out_infinite] bg-clip-text text-transparent font-bold">
                            VIP
                          </span>
                        </Badge>
                      )}
                    </div>
                    <button
                      onClick={handleSavePreset}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all",
                        isPremium
                          ? "border-marquee-bulb/40 text-marquee-bulb hover:bg-marquee-bulb/10"
                          : "border-accent/40 text-accent/80 hover:bg-accent/10"
                      )}
                    >
                      {isPremium ? <BookmarkPlus className="h-3.5 w-3.5" /> : <Lock className="h-3 w-3" />}
                      Save this setup
                    </button>
                  </div>

                  {isPremium ? (
                    presets.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {presets.map((preset) => (
                          <span
                            key={preset.id}
                            className="group inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full border border-marquee-bulb/30 bg-background/40 text-[11px] font-bold uppercase tracking-wide text-foreground/75"
                          >
                            <button onClick={() => applyPreset(preset)} className="hover:text-marquee-bulb transition-colors">
                              {preset.name}
                            </button>
                            <button
                              onClick={() => deletePreset(preset.id)}
                              aria-label={`Remove ${preset.name}`}
                              className="text-foreground/35 hover:text-destructive transition-colors"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-foreground/50">
                        Dial in a combination you love, then keep it on your shelf — up to {MAX_PRESETS}.
                      </p>
                    )
                  ) : (
                    <p className="text-[10px] text-foreground/50">
                      VIP members keep up to {MAX_PRESETS} favourite mood-and-dial combinations one tap away.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ============ ADMIT ONE — primary CTA ============ */}
            <div className="flex flex-col sm:flex-row gap-2 pt-1" data-tutorial="discover-button">
              <Button
                onClick={handleDiscover}
                disabled={
                  (selectedMoods.length === 0 &&
                    !textQuery.trim() &&
                    !keywords.trim() &&
                    selectedGenres.length === 0) ||
                  isLoading ||
                  isSearchLimitReached ||
                  featureClosed
                }
                size="lg"
                className={cn(
                  "relative flex-1 h-14 sm:h-16 text-base sm:text-lg font-black uppercase tracking-[0.15em] overflow-hidden projector-sweep",
                  "bg-gradient-to-r from-marquee-bulb via-accent to-marquee-bulb bg-[length:200%_100%]",
                  "text-background hover:bg-[length:100%_100%]",
                  "shadow-[0_0_30px_-6px_hsl(var(--marquee-bulb)/0.6),0_8px_24px_-8px_hsl(0_0%_0%/0.7)]",
                  "hover:-translate-y-0.5 active:scale-[0.98]",
                  "border-0 rounded-xl",
                  "disabled:opacity-40 disabled:hover:translate-y-0"
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : isSearchLimitReached ? (
                  <Lock className="h-5 w-5 mr-2" />
                ) : (
                  <span className="mr-2 inline-flex items-center justify-center h-6 w-6 rounded-full border-2 border-background/70">
                    <Search className="h-3.5 w-3.5" />
                  </span>
                )}
                {isLoading
                  ? "Rolling the reel…"
                  : isSearchLimitReached
                  ? "Discovery unavailable"
                  : "Admit one · Find my film"}
              </Button>

              <Button
                onClick={handleSurpriseMe}
                disabled={isLoading || isSearchLimitReached || featureClosed}
                variant="outline"
                size="lg"
                className="h-14 sm:h-16 px-5 sm:px-7 border-marquee-bulb/40 text-foreground/85 hover:bg-marquee-bulb/10 hover:border-marquee-bulb/70 hover:text-foreground font-bold uppercase tracking-[0.15em] text-xs sm:text-sm rounded-xl"
              >
                <Shuffle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Roll the dice</span>
              </Button>
            </div>

            {/* Limit reached */}
            {isSearchLimitReached && !isPremium && (
              <div className="vip-invitation rounded-md p-4 text-left">
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex shrink-0 items-center gap-2 rounded-sm border border-marquee-bulb/20 bg-background/40 px-3 py-2">
                    {[0, 1, 2].map((light) => <span key={light} className="vip-control-light" style={{ animationDelay: `${light * 2}s` }} />)}
                    <span className="ml-1 text-[9px] font-black uppercase tracking-[0.2em] text-marquee-bulb">Reel meter</span>
                  </div>
                  <p className="flex-1 text-sm font-medium">
                    Tonight's reel is spent — {searchLimit} searches used this month
                  </p>
                  <Link to="/vip?from=quick-discovery" className="shrink-0">
                    <Button size="sm" variant="screen" className="w-full sm:w-auto">
                      <Crown className="h-4 w-4 mr-2" /> Upgrade for 100 searches/month
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {/* Re-roll the same request — free for VIP, never spends a search */}
            {lastParams && !isSearchLimitReached && (
              <button
                onClick={handleReroll}
                disabled={isLoading || featureClosed}
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-2 rounded-lg border transition-all text-[11px] font-bold uppercase tracking-[0.18em] disabled:opacity-40",
                  isPremium
                    ? "border-marquee-bulb/40 text-marquee-bulb hover:bg-marquee-bulb/10"
                    : "border-accent/40 text-accent/80 hover:bg-accent/10"
                )}
              >
                {isPremium ? <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} /> : <Crown className="h-3.5 w-3.5 text-premium" />}
                {isPremium
                  ? rerolls > 0
                    ? `Re-roll the reel · free (${rerolls})`
                    : 'Re-roll the reel · free, no search used'
                  : 'Re-roll the reel · VIP'}
              </button>
            )}

            {/* Depth note — VIP gets a deeper reel and reasoning per pick */}
            {!isSearchLimitReached && (
              <p className="text-[11px] text-center text-foreground/50">
                {isPremium
                  ? 'VIP reel: up to 30 picks per search, each with a reason for the match — and re-rolls are free.'
                  : 'Free reel: 12 picks per search. VIP gets 30 picks, stacked moods, vault depth, free re-rolls, saved setups and a reason for every match.'}
              </p>
            )}

            {!isPremium && (
              <div className="film-strip-top film-strip-bottom rounded-lg border border-marquee-bulb/25 bg-background/40 px-4 py-3 text-center">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-marquee-bulb">Projection booth upgrade</p>
                <p className="mt-1 text-xs text-foreground/65">1 mood → 5 · 2 genres → unlimited · 12 results → 30 · deeper Vault · saved setups</p>
                <Button variant="link" size="sm" className="mt-1 text-marquee-bulb" onClick={() => navigate('/vip?from=quick-discovery')}>
                  See the VIP programme
                </Button>
              </div>
            )}
            {!isPremium && !isSearchLimitReached && (
              <p className="text-[10px] text-center text-accent/80">
                VIP previews left this month: {vipTasteRemaining} of {vipTasteLimit}
              </p>
            )}

            {/* Active filter summary */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between pt-3 border-t border-marquee-bulb/15 text-[11px]">
                <div className="flex items-center gap-2 text-foreground/55 uppercase tracking-wider">
                  <Filter className="h-3 w-3" />
                  <span className="truncate">
                    {[
                      selectedMoods.length > 0 &&
                        `${selectedMoods.length} mood${selectedMoods.length > 1 ? "s" : ""}`,
                      selectedGenres.length > 0 &&
                        `${selectedGenres.length} genre${selectedGenres.length > 1 ? "s" : ""}`,
                      textQuery && "clue",
                      language && LANGUAGES.find((l) => l.code === language)?.label,
                      (runtime[0] !== 0 || runtime[1] !== 300) && "runtime",
                      obscurity !== null && getObscurityLabel(obscurity),
                      era !== null && getEraLabel(era),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <button
                  onClick={clearAllFilters}
                  className="text-foreground/55 hover:text-foreground flex items-center gap-1 uppercase tracking-wider"
                >
                  <X className="h-3 w-3" />
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom marquee bulb strip */}
        <div
          aria-hidden
          className="absolute -bottom-2 left-6 right-6 h-1 flex justify-between items-center px-2 z-20"
        >
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="marquee-bulb"
              style={{ animationDelay: `${(i % 6) * 0.4 + 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};